import {formatDate} from '../../utils/formatDate'

export const formatPercent = (value: number | null): string =>
  value === null ? '—' : `${(value * 100).toFixed(1)}%`

/**
 * Render a (possibly malformed) RFC3339 timestamp.
 *
 * A non-null but unparseable value would make `new Date(...)` an Invalid Date,
 * which Intl throws a RangeError on — so validate the parse before formatting.
 * Mirrors FaceConfidenceModal's formatTrainedLabel.
 */
export const formatTimestamp = (value: string | null): string => {
  const parsed = value ? new Date(value) : null
  return parsed && !Number.isNaN(parsed.getTime())
    ? formatDate(parsed, 'longString')
    : 'unknown'
}

/**
 * Label for a sampled photo thumbnail.
 *
 * The album name carries the human meaning — a bare photo id is an internal
 * primary key — so it leads when present, with the id kept for cross-checking
 * against the album view. Falls back to the id alone when the album has no
 * name (a legitimate NULL in the source data, not an error).
 */
export const formatPhotoLabel = (albumName: string, photoId: number): string =>
  albumName ? `${albumName} · photo ${photoId}` : `Photo ${photoId}`

/**
 * Resolve a tagger's display name, falling back to "Person <id>" when the
 * persons batch lookup didn't return one — name resolution is a display
 * enhancement and must never blank out the row.
 */
export const resolveTaggerName = (
  taggingPersonId: number | null,
  nameMap: Map<number, string>,
  unknownLabel: string,
): string => {
  if (taggingPersonId === null) return unknownLabel
  return nameMap.get(taggingPersonId) || `Person ${taggingPersonId}`
}
